import React, { useState } from 'react'
import { withRouter, Link } from 'react-router-dom'
import { client, MAX_LATE_DEDUCTIONS } from '@Utils'
import { useStateValue } from '@State'
import { LtiRedirect } from './LtiRedirect'
import { toast } from '@CommonComponents/toast'
import { ActivitiesContainer } from './activities/Container'
import { ReadingLinksContainer } from './readinglinks/Container'
import { buildReadingNodes } from './readinglinks/payload'
import { Form } from 'react-bootstrap'

// --- Late Policy Settings helpers ---

const parseLateDeductions = raw => {
  if (!raw) return []

  var arr = raw

  // If it's a string like "[[1,'d',100.0,'percent']]"
  if (typeof arr === 'string') {
    try {
      // Convert single quotes to double quotes for JSON.parse
      var jsonStr = arr.replace(/'/g, '"')
      arr = JSON.parse(jsonStr)
    } catch (e) {
      console.warn('Could not parse defaultLatePolicyDeductions:', raw, e)
      return []
    }
  }

  if (!Array.isArray(arr)) return []

  return arr.map(function (inner) {
    inner = Array.isArray(inner) ? inner : []

    return {
      offset:
        inner.length > 0 && inner[0] !== null && inner[0] !== undefined
          ? inner[0]
          : '',
      unit:
        inner.length > 1 && inner[1] !== null && inner[1] !== undefined
          ? inner[1]
          : 'd',
      amount:
        inner.length > 2 && inner[2] !== null && inner[2] !== undefined
          ? inner[2]
          : '',
      mode:
        inner.length > 3 && inner[3] !== null && inner[3] !== undefined
          ? inner[3]
          : 'percent',
    }
  })
}

const serializeLateDeductions = rows => {
  if (!rows || !rows.length) return ''

  // Only include rows that have both an offset and amount
  const inner = rows
    .filter(r => r.offset !== '' && r.amount !== '')
    .map(r => {
      const offset = Number(r.offset) || 0
      const amount = Number(r.amount) || 0
      const unit = r.unit || 'd'
      const mode = r.mode || 'percent'
      return `[${offset},'${unit}',${amount},'${mode}']`
    })

  if (!inner.length) return ''
  return `[${inner.join(',')}]`
}

const ResourceSelectionComp = () => {
  const [currentState, dispatch] = useStateValue()
  const [redirect, setRedirect] = useState(null)
  const [selectedTitleIds, setSelectedTitleIds] = useState([])
  const [selectedActIds, setSelectedActIds] = useState([])
  const [selectedReadingIds, setSelectedReadingIds] = useState([])

  // Initialize late policy settings from context settings
  const contextSettings = currentState.contextSettings || {}
  const initialDeductions = parseLateDeductions(
    contextSettings.defaultLatePolicyDeductions || ''
  )

  // Late policy settings state
  const [defaultLatePolicy, setDefaultLatePolicy] = useState(
    contextSettings.defaultLatePolicy || ''
  )
  const [lateDeductions, setLateDeductions] = useState(
    initialDeductions.length > 0 ? initialDeductions : []
  )
  const [isSavingSettings, setIsSavingSettings] = useState(false)

  const addResource = async () => {
    try {
      const resp = await client.post(
        '/lti13/resource_selection.get_resource_jwt/',
        {
          title_page_links: selectedTitleIds,
          activity_ids: selectedActIds,
          reading_nodes: buildReadingNodes(
            selectedReadingIds,
            currentState.connectedCourses
          ),
        }
      )
      setRedirect(resp.data) // this triggers the redirect and form auto-submission
    } catch (err) {
      let errMsg = err
      if (err.response) {
        errMsg = err.response.data.error
      }
      toast.error(errMsg)
      setRedirect(null)
    }
  }

  const handleActCheck = event => {
    const ele = event.target
    const targetId = `${ele.value}-${ele.attributes['data-sort-idx'].value}`
    const itemIdx = selectedActIds.indexOf(targetId)
    if (itemIdx > -1) {
      selectedActIds.splice(itemIdx, 1)
    } else {
      selectedActIds.push(targetId)
    }
    if (currentState.multiSelectEnabled) {
      setSelectedActIds([...selectedActIds])
    } else {
      setSelectedActIds([targetId])
      setSelectedTitleIds([])
      setSelectedReadingIds([])
    }
  }

  const handleReadingCheck = event => {
    const ele = event.target
    const targetId = `${ele.value}::${ele.attributes['data-sort-idx'].value}`
    const itemIdx = selectedReadingIds.indexOf(targetId)
    if (itemIdx > -1) {
      selectedReadingIds.splice(itemIdx, 1)
    } else {
      selectedReadingIds.push(targetId)
    }
    if (currentState.multiSelectEnabled) {
      setSelectedReadingIds([...selectedReadingIds])
    } else {
      setSelectedReadingIds([targetId])
      setSelectedTitleIds([])
      setSelectedActIds([])
    }
  }

  const handleTitleCheck = event => {
    const ele = event.target
    const targetId = `${ele.value}-${ele.attributes['data-sort-idx'].value}`
    const itemIdx = selectedTitleIds.indexOf(targetId)
    if (itemIdx > -1) {
      selectedTitleIds.splice(itemIdx, 1)
    } else {
      selectedTitleIds.push(targetId)
    }
    if (currentState.multiSelectEnabled) {
      setSelectedTitleIds([...selectedTitleIds])
    } else {
      setSelectedTitleIds([targetId])
      setSelectedReadingIds([])
      setSelectedActIds([])
    }
  }

  // --- Late Policy Settings handlers ---

  const handleLateDeductionChange = (index, field, value) => {
    const updated = lateDeductions.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    )
    setLateDeductions(updated)
  }

  const addLateDeductionRow = () => {
    if (lateDeductions.length >= MAX_LATE_DEDUCTIONS) return
    setLateDeductions([
      ...lateDeductions,
      { offset: '1', unit: 'd', amount: '100.0', mode: 'percent' },
    ])
  }

  const removeLateDeductionRow = index => {
    const updated = lateDeductions.filter((_, i) => i !== index)
    setLateDeductions(updated)
  }

  // Check if deductions are valid (at least one row with offset and amount)
  const hasValidDeductions = lateDeductions.some(
    row => row.offset !== '' && row.amount !== ''
  )

  // Validation: if policy is 'deduct', must have at least one valid deduction row
  const isDeductWithoutDeductions =
    defaultLatePolicy === 'deduct' && !hasValidDeductions

  const saveLatePolicySettings = async () => {
    setIsSavingSettings(true)
    try {
      const serializedDeductions = serializeLateDeductions(lateDeductions)
      const snapSettings = {
        defaultLatePolicy: defaultLatePolicy,
        defaultLatePolicyDeductions: serializedDeductions,
      }

      await client.post('/lti13/resource_selection.save_context_settings/', {
        snap: snapSettings,
      })

      toast.success('Late policy settings saved successfully.')
    } catch (err) {
      console.error(err)
      let errMsg = 'There was an error saving the settings.'
      if (err.response) {
        errMsg = `Error: ${err.response.data.error}`
      }
      toast.error(errMsg)
    } finally {
      setIsSavingSettings(false)
    }
  }

  const helpTextStyle = {
    fontSize: '0.8em',
    color: '#6B5400',
  }

  return (
    <React.Fragment>
      <div className="mt-5">
        {!currentState.assessmentsOnly && !currentState.readingLinksOnly && (
          <div>
            <ul className="nav nav-tabs nav-tabs-embedded">
              <li
                className={
                  currentState.currentPage === 'reading' ? 'active' : ''
                }
              >
                <a
                  onClick={() => {
                    dispatch({
                      type: 'updateCurrentPage',
                      currentPage: 'reading',
                    })
                  }}
                >
                  Reading Links
                </a>
              </li>
              <li
                className={
                  currentState.currentPage === 'assignment' ? 'active' : ''
                }
              >
                <a
                  onClick={() => {
                    dispatch({
                      type: 'updateCurrentPage',
                      currentPage: 'assignment',
                    })
                  }}
                >
                  Assessments
                </a>
              </li>
              {contextSettings.showSettingsTab && (
                <li
                  className={
                    currentState.currentPage === 'settings' ? 'active' : ''
                  }
                >
                  <a
                    onClick={() => {
                      dispatch({
                        type: 'updateCurrentPage',
                        currentPage: 'settings',
                      })
                    }}
                  >
                    Settings
                  </a>
                </li>
              )}
            </ul>
          </div>
        )}
        {
          {
            assignment: (
              <ActivitiesContainer
                selectedResourceIds={selectedActIds}
                handleCheck={handleActCheck}
              />
            ),
            reading: (
              <ReadingLinksContainer
                selectedResourceIds={selectedReadingIds}
                handleCheck={handleReadingCheck}
                selectedTitleIds={selectedTitleIds}
                handleTitleCheck={handleTitleCheck}
              />
            ),
            settings: contextSettings.showSettingsTab ? (
              <div className="p-4">
                <h4>Late Policy Settings</h4>
                <p className="text-muted">
                  Configure default late policy settings for this context. These
                  settings will be applied to new resource links created within
                  this LMS course.
                </p>

                <Form.Group
                  controlId="defaultLatePolicy"
                  style={{ maxWidth: '350px' }}
                >
                  <Form.Label>Default Late Policy:</Form.Label>
                  <Form.Control
                    as="select"
                    onChange={e => setDefaultLatePolicy(e.target.value)}
                    name="defaultLatePolicy"
                    value={defaultLatePolicy}
                  >
                    <option value="">No Policy</option>
                    <option value="flag">Flag</option>
                    <option value="prevent">Prevent</option>
                    <option value="deduct">Deduct</option>
                  </Form.Control>
                  <div style={helpTextStyle}>
                    Flag as late only, prevent submissions, or deduct points
                    according to deductions
                  </div>
                </Form.Group>

                {defaultLatePolicy === 'deduct' && (
                  <Form.Group controlId="defaultLatePolicyDeductions">
                    <Form.Label>Default Late Policy Deductions:</Form.Label>

                    {/* Column headers */}
                    <div
                      className="d-flex mb-1"
                      style={{
                        fontSize: '0.8em',
                        fontWeight: 'bold',
                        maxWidth: '420px',
                      }}
                    >
                      <div style={{ width: '70px', marginRight: '4px' }}>
                        After
                      </div>
                      <div style={{ width: '100px', marginRight: '4px' }}>
                        Unit
                      </div>
                      <div style={{ width: '80px', marginRight: '4px' }}>
                        Amount
                      </div>
                      <div style={{ width: '100px', marginRight: '4px' }}>
                        Type
                      </div>
                      <div style={{ width: '35px' }} />
                    </div>

                    {/* Rows */}
                    {lateDeductions.map((row, index) => (
                      <div
                        className="d-flex mb-2"
                        key={index}
                        style={{ maxWidth: '420px' }}
                      >
                        {/* Offset (int) */}
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          placeholder="1"
                          value={row.offset}
                          onChange={e =>
                            handleLateDeductionChange(
                              index,
                              'offset',
                              e.target.value
                            )
                          }
                          style={{ width: '70px', marginRight: '4px' }}
                        />

                        {/* Unit: d/h/m */}
                        <Form.Control
                          as="select"
                          value={row.unit}
                          onChange={e =>
                            handleLateDeductionChange(
                              index,
                              'unit',
                              e.target.value
                            )
                          }
                          style={{ width: '100px', marginRight: '4px' }}
                        >
                          <option value="d">days</option>
                          <option value="h">hours</option>
                          <option value="m">minutes</option>
                        </Form.Control>

                        {/* Amount (decimal) */}
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="100"
                          value={row.amount}
                          onChange={e =>
                            handleLateDeductionChange(
                              index,
                              'amount',
                              e.target.value
                            )
                          }
                          style={{ width: '80px', marginRight: '4px' }}
                        />

                        {/* Mode: points/percent */}
                        <Form.Control
                          as="select"
                          value={row.mode}
                          onChange={e =>
                            handleLateDeductionChange(
                              index,
                              'mode',
                              e.target.value
                            )
                          }
                          style={{ width: '100px', marginRight: '4px' }}
                        >
                          <option value="points">points</option>
                          <option value="percent">percent</option>
                        </Form.Control>

                        {/* Remove row */}
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={() => removeLateDeductionRow(index)}
                          title="Remove this deduction"
                          style={{ width: '35px' }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}

                    {/* Add row button */}
                    {lateDeductions.length < MAX_LATE_DEDUCTIONS && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mb-2"
                        onClick={addLateDeductionRow}
                      >
                        + Add deduction
                      </button>
                    )}

                    <div style={helpTextStyle}>
                      Define when and how much to deduct for late submissions.
                      Each row specifies a time threshold and the penalty to
                      apply. For example: &quot;After 1 days, deduct 50
                      percent&quot; means submissions more than 1 day late lose
                      50% of their score. Add multiple rows for tiered
                      penalties.
                    </div>
                  </Form.Group>
                )}

                {isDeductWithoutDeductions && (
                  <div className="alert alert-warning mb-3">
                    When using &quot;Deduct&quot; policy, you must add at least
                    one deduction rule with both &quot;After&quot; and
                    &quot;Amount&quot; values.
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-success btn-myed btn-success-myed"
                  onClick={saveLatePolicySettings}
                  disabled={isSavingSettings || isDeductWithoutDeductions}
                >
                  {isSavingSettings ? (
                    <span>
                      <i className="fa-regular fa-spinner fa-spin fa-fw mr-2" />{' '}
                      Saving...
                    </span>
                  ) : (
                    <span>
                      <i className="fa-regular fa-save fa-fw mr-2" /> Save
                      Settings
                    </span>
                  )}
                </button>
              </div>
            ) : null,
          }[currentState.currentPage]
        }

        {currentState.currentPage !== 'settings' && (
          <React.Fragment>
            <hr />

            <div className="d-flex justify-content-between">
              <div className="pt-1">
                <button
                  disabled={
                    selectedActIds.length === 0 &&
                    selectedReadingIds.length === 0 &&
                    selectedTitleIds.length == 0
                  }
                  className="btn btn-myed btn-primary btn-primary-myed"
                  onClick={addResource}
                >
                  Add{' '}
                  {selectedActIds.length +
                    selectedReadingIds.length +
                    selectedTitleIds.length}{' '}
                  Link
                  {selectedActIds.length +
                    selectedReadingIds.length +
                    selectedTitleIds.length ==
                  1
                    ? ''
                    : 's'}
                </button>
                <div className="meta-info-container">
                  {(selectedTitleIds.length > 0 ||
                    selectedActIds.length > 0 ||
                    selectedReadingIds.length > 0) && (
                    <ul>
                      {selectedTitleIds.length > 0 && (
                        <li>
                          {`${selectedTitleIds.length} title link${
                            selectedTitleIds.length > 1 ? 's' : ''
                          } selected`}
                        </li>
                      )}
                      {selectedReadingIds.length > 0 && (
                        <li>
                          {`${selectedReadingIds.length} reading link${
                            selectedReadingIds.length > 1 ? 's' : ''
                          } selected`}
                        </li>
                      )}
                      {selectedActIds.length > 0 && (
                        <li>
                          {`${selectedActIds.length} assignment${
                            selectedActIds.length > 1 ? 's' : ''
                          } selected`}
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                <LtiRedirect
                  route_method="POST"
                  route_action={redirect && redirect.route_action}
                  route_data={redirect && redirect.route_data}
                />
              </div>
            </div>
            <div className="pull-right mr-4">
              <Link
                variant="gray-primary"
                to={`/lti13/resource_selection/-/${currentState.contextObjId}/connect_resource${window.location.search}`}
              >
                Connect Another Resource
              </Link>
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  )
}

export const ResourceSelection = withRouter(ResourceSelectionComp)
